import React, { useState, useEffect, useRef } from 'react';

const SearchableSelectUI = ({
  id,
  label,
  placeholder,
  searchTerm,
  onSearchChange,
  results,
  isSearching,
  selectedItem,
  onItemSelect,
  onClear,
  disabled = false,
  clearable = true,
  renderItem,
  renderSelectedItem
}) => {
  // Default renderers if not provided
  const defaultRenderItem = (item) => (
    <>
      <div className="font-medium">{item.title || item.name}</div>
      {(item.company || item.email) && (
        <div className="text-sm text-gray-600">{item.company || item.email}</div>
      )}
    </>
  );

  const defaultRenderSelectedItem = (item) => (
    <>
      <div className="font-medium">{item.title || item.name}</div>
      {(item.company || item.email) && (
        <div className="text-sm text-gray-600">{item.company || item.email}</div>
      )}
    </>
  );

  // State to track the currently focused item index
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const resultsContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Use provided render functions or defaults
  const renderItemFn = renderItem || defaultRenderItem;
  const renderSelectedItemFn = renderSelectedItem || defaultRenderSelectedItem;

  // Reset focused index when results change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [results]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!results.length || selectedItem || disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prevIndex => {
          const newIndex = prevIndex < results.length - 1 ? prevIndex + 1 : 0;
          scrollIntoView(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prevIndex => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : results.length - 1;
          scrollIntoView(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < results.length) {
          onItemSelect(results[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Scroll the focused item into view
  const scrollIntoView = (index) => {
    if (resultsContainerRef.current && index >= 0) {
      const container = resultsContainerRef.current;
      const item = container.children[index];
      if (item) {
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.clientHeight;

        if (itemTop < containerTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
          container.scrollTop = itemBottom - container.clientHeight;
        }
      }
    }
  };

  return (
    <div>
      <label htmlFor={id} className="block text-base font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        {selectedItem ? (
          <div className="flex items-center">
            <div className="block w-full rounded-lg text-base px-4 py-3 border border-gray-300 bg-gray-50 text-gray-700 shadow-sm">
              {renderSelectedItemFn(selectedItem)}
            </div>
            {clearable && !disabled && (
              <button
                type="button"
                onClick={onClear}
                className="absolute right-2 text-gray-500 hover:text-gray-700"
                aria-label="Clear selection"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            id={id}
            value={searchTerm}
            onChange={onSearchChange}
            onKeyDown={handleKeyDown}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder={placeholder}
            disabled={disabled}
            autocomplete="off"
            aria-autocomplete="list"
            aria-controls={results.length > 0 ? `${id}-results` : undefined}
            aria-activedescendant={focusedIndex >= 0 ? `${id}-result-${focusedIndex}` : undefined}
          />
        )}
        
        {isSearching && (
          <div className="absolute right-3 top-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {results.length > 0 && !selectedItem && !disabled && (
          <div 
            ref={resultsContainerRef}
            id={`${id}-results`}
            role="listbox"
            className="absolute z-10 w-full mt-2 bg-white shadow-lg rounded-lg border border-gray-300 max-h-60 overflow-y-auto"
          >
            {results.map((item, index) => (
              <div
                key={item.id || item.job_id || item.talent_id || index}
                id={`${id}-result-${index}`}
                role="option"
                aria-selected={focusedIndex === index}
                className={`px-4 py-3 cursor-pointer text-base ${focusedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                onClick={() => onItemSelect(item)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                {renderItemFn(item)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableSelectUI;
